/**
 * Support Email Utilities
 *
 * Builds mailto links with debug context for transaction errors.
 * Enable via `showSupportEmail: true` in handlingParams (V1) or as a prop (V2).
 *
 * FLOWS WITH SUPPORT EMAIL ENABLED:
 *
 * V1 Guest Checkout (post-commit transaction failures):
 *   - usePollForGuestCheckoutTransaction.ts (BUY_FAILED, SEND_FAILED, EXPIRED, INVALID, FAILED)
 *   - order-preview.page.tsx (permission denied, commit errors)
 *
 * V1 Authenticated (post-commit transaction failures):
 *   - initiating-purchase.page.tsx (buy/send failures)
 *   - return-3ds.page.tsx (3DS failures)
 *   - verify-identity.page.tsx (2FA/send failures)
 *   - useApplePayBuySubmitter.ts (Apple Pay failures)
 *
 * V2 Guest Checkout (post-commit transaction failures):
 *   - GuestTransactionStatusPollerPageV2.tsx (polling errors)
 *   - OrderPreviewView.tsx (commit failures)
 */

import { transactionIdAtom } from '@onramp/state/recoil/atoms/attributionAtoms';
import { createTransferErrorAtom } from '@onramp/state/recoil/atoms/oneClickBuyAtoms';
import { jotaiStore } from '@onramp/state/recoil/utils';
import { clientSessionIdStore } from '@onramp/utils/clientSessionIdStore';
import { guestOnrampV2StoreAtom } from '@onramp/v2/client/views/guest/onramp/state/store';

import getAppVersion from '../../appVersion';

export const ONRAMP_SUPPORT_EMAIL = 'onrampsupport@coinbase.com';

export type SupportEmailFlowType = 'guest' | 'authed';

/**
 * Infers the flow type based on 3 sources of truth:
 * 1. guestCheckoutEntityHash - set after phone verification in guest checkout
 * 2. buyFlowType - set to 'regular', 'one-click-buy', or 'recent-transactions' in authed flows
 * 3. userUuid - set from server middleware for authenticated users
 *
 * @returns 'guest' if in guest checkout flow, 'authed' otherwise
 */
function inferFlowType(): SupportEmailFlowType {
  // 1. Check for guest entity hash (set after phone verification in GCO)
  const guestEntityHash = clientSessionIdStore.getGuestCheckoutEntityHash();
  if (guestEntityHash) {
    return 'guest';
  }

  // 2. Check for explicit authed flow types
  const buyFlowType = clientSessionIdStore.getBuyFlowType();
  if (
    buyFlowType === 'regular' ||
    buyFlowType === 'one-click-buy' ||
    buyFlowType === 'recent-transactions'
  ) {
    return 'authed';
  }

  // 3. Check if user is authenticated (has userUuid from server)
  const userUuid = clientSessionIdStore.getUserUuid();
  if (userUuid) {
    return 'authed';
  }

  // Default to guest if we can't determine (early guest flow errors before phone verification)
  return 'guest';
}

/**
 * Gets common snapshot data shared between both flows.
 */
function getCommonSnapshot(): Record<string, string | undefined> {
  const clientAppDetails = clientSessionIdStore.getClientAppDetails();

  return {
    // Session
    sessionId: clientSessionIdStore.getClientSessionId(),
    // App/Partner info
    appId: clientSessionIdStore.getAppId(),
    partnerName: clientAppDetails?.displayName,
    // Platform info
    deviceId: clientSessionIdStore.getDeviceId(),
    platformAttribution: clientSessionIdStore.getPlatformAttribution(),
    platformVersion: clientSessionIdStore.getPlatformVersion(),
    sdkVersion: clientSessionIdStore.getSdkVersion(),
  };
}

/**
 * Gets guest checkout specific snapshot data.
 */
function getGuestCheckoutSnapshot(): Record<string, string | undefined> {
  const guestV2Store = jotaiStore.get(guestOnrampV2StoreAtom);

  return {
    // Guest identifiers
    guestEntityHash: clientSessionIdStore.getGuestCheckoutEntityHash(),
    guestSessionUuid: guestV2Store?.persistedGuestSession?.sessionUuid,
    // V2 Guest Checkout transaction data
    guestTransactionIdAtCreate: guestV2Store?.createGCTransactionData?.uuid,
    guestTransactionIdAtCommit: guestV2Store?.commitGCTransactionData?.uuid,
    guestCommitStatus: guestV2Store?.commitGCTransactionData?.status,
    guestPolledError: guestV2Store?.polledTransactionData?.error ?? undefined,
    guestPolledStatus: guestV2Store?.polledTransactionData?.guestCheckoutTransaction?.status,
    // Guest transaction details
    guestAsset: guestV2Store?.selectedAsset?.symbol,
    guestNetwork: guestV2Store?.selectedNetwork,
    guestAmount: guestV2Store?.inputAmount?.userAmount?.value,
    guestCurrency: guestV2Store?.inputAmount?.userAmount?.currency,
    guestPaymentMethod: guestV2Store?.paymentMethod,
  };
}

/**
 * Gets authenticated flow specific snapshot data.
 */
function getAuthedFlowSnapshot(): Record<string, string | undefined> {
  const buyWidgetState = clientSessionIdStore.getBuyWidgetState();

  return {
    // User identifier
    userUuid: clientSessionIdStore.getUserUuid() || undefined,
    // Transaction IDs
    onrampTransactionId: jotaiStore.get(transactionIdAtom),
    createTransferError: jotaiStore.get(createTransferErrorAtom),
    authedBuyUuid: buyWidgetState?.createBuyResponse?.uuid,
    authedSendUuid: buyWidgetState?.createSendResponse?.uuid,
    walletTransactionId: buyWidgetState?.walletTransactionUuid,
    // Transaction details
    authedAsset: buyWidgetState?.selectedAsset?.ticker,
    authedNetwork: buyWidgetState?.selectedNetwork?.name,
    authedAmount: buyWidgetState?.inputAmount,
    authedAmountType: buyWidgetState?.inputAmountType,
    authedPolledSendStatus: buyWidgetState?.polledSendStatus,
    authedPaymentMethodType: buyWidgetState?.sourceOfFunds?.paymentMethodType,
    authedSourceOfFundsType: buyWidgetState?.sourceOfFunds?.type,
    // Flow metadata
    authedFlowType: clientSessionIdStore.getBuyFlowType(),
    authedTransferType: clientSessionIdStore.getTransferType(),
    authenticationMethod: clientSessionIdStore.getAuthenticationMethod(),
  };
}

/**
 * Gets the flow-specific snapshot based on inferred flow type.
 */
function getFlowSnapshot(flowType: SupportEmailFlowType): Record<string, string | undefined> {
  const common = getCommonSnapshot();
  const flowSpecific = flowType === 'guest' ? getGuestCheckoutSnapshot() : getAuthedFlowSnapshot();
  return { ...common, ...flowSpecific };
}

/**
 * Builds the support email context from client session store and additional context.
 * Automatically infers the flow type and includes only relevant data.
 *
 * @param additionalContext - Additional context to merge (any key-value pairs)
 * @returns Complete support email context as key-value pairs
 */
export function buildSupportEmailContext(
  additionalContext?: Record<string, string | undefined>,
): Record<string, string | undefined> {
  const flowType = inferFlowType();
  const flowSnapshot = getFlowSnapshot(flowType);

  return {
    flowType,
    ...flowSnapshot,

    // App version and timestamp
    appVersion: getAppVersion(),
    timestamp: new Date().toISOString(),

    // Browser/environment info
    locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

    // Merge any additional context from caller (error details, etc.)
    ...additionalContext,
  };
}

/**
 * Builds a mailto link with pre-populated subject and body containing debug context.
 *
 * The email body includes non-sensitive debug information to help support
 * teams quickly identify and resolve issues.
 *
 * @param additionalContext - Additional context to include in the email
 * @returns A mailto URL string
 */
export function buildSupportEmail(additionalContext?: Record<string, string | undefined>): string {
  const context = buildSupportEmailContext(additionalContext);
  const subject = buildEmailSubject();
  const body = buildEmailBody(context);

  return `mailto:${ONRAMP_SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

/**
 * Builds the email subject line with flow type and identifier for easier ticket tracking.
 * Uses guestCheckoutEntityHash for guest flows, userUuid for authed flows.
 */
function buildEmailSubject(): string {
  const flowType = inferFlowType();
  const guestCheckoutEntityHash = clientSessionIdStore.getGuestCheckoutEntityHash();
  const userUuid = clientSessionIdStore.getUserUuid();

  const identifier = flowType === 'guest' ? guestCheckoutEntityHash : userUuid;
  const subjectPrefix =
    flowType === 'guest' ? 'Guest Checkout Support Request' : 'Onramp Support Request';

  const parts = [subjectPrefix];

  if (identifier) {
    parts.push(`[${identifier}]`);
  }

  return parts.join(' ');
}

/**
 * Builds the email body with debug context.
 * Filters out undefined values and formats as key: value pairs.
 */
function buildEmailBody(context: Record<string, string | undefined>): string {
  // Filter out undefined/empty values and format as key: value
  const debugLines = Object.entries(context)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${key}: ${value}`);

  const debugSection =
    debugLines.length > 0
      ? `
--- Debug Information (please do not edit) ---
${debugLines.join('\n')}
---
        `
      : '';

  return `
[Please describe your issue here]

${debugSection}`;
}
